import { castArray } from 'lodash/fp';
import { navigateToWorkbench } from 'app/routes';
import { selectEntitiesById, selectEntityByPropId } from 'app/store/entities/selectors.js';
import {
    selectCurrentProjectId,
    selectProject,
    selectSelectedEntityId
} from 'app/store/projects/selectors.js';

import {
    selectGroups,
    selectSelectedNodesForCurrentProject,
    selectGroupedDependencyGraph
} from './selectors.js';
import {
    SET_DEFAULT_FLOW_MODE,
    CLEAR_SELECTION,
    COLLAPSE_ALL_GROUPS,
    CREATE_NODE_GROUP,
    DELETE_NODE_GROUP,
    EXPAND_ALL_GROUPS,
    RENAME_NODE_GROUP,
    TOGGLE_GROUP,
    TOGGLE_SELECTION,
    AREA_SELECTION,
    FROM_TO_SELECTION,
    UPDATE_NODE_GROUP_DESCRIPTION,
    SET_BULK_GROUP_EXPANSION,
    NAVIGATE_TO_NODE
} from './types.js';

export const doSetDefaultFlowMode = (mode) => ({
    type: SET_DEFAULT_FLOW_MODE,
    payload: (state) => {
        const projectId = selectCurrentProjectId(state);

        return { projectId, mode };
    }
});

export const doToggleSelection = ({ ids }) => ({
    type: TOGGLE_SELECTION,
    payload: (state) => {
        const selection = selectSelectedNodesForCurrentProject(state);
        const currentNodeId = selectSelectedEntityId(state)?.split('.')?.at(-1);
        const projectId = selectCurrentProjectId(state);
        const nodeIds = castArray(ids);

        if (selection.length === 0 && currentNodeId) {
            nodeIds.unshift(currentNodeId);
        }

        return { projectId, nodeIds };
    }
});

export const doAreaSelection = (nodeIds) => ({
    type: AREA_SELECTION,
    payload: (state) => {
        const projectId = selectCurrentProjectId(state);

        return { nodeIds, projectId };
    }
});

export const doFromToSelection = ({ to }) => ({
    type: FROM_TO_SELECTION,
    payload: (state) => {
        const projectId = selectCurrentProjectId(state);
        const currentNodeId = selectSelectedEntityId(state)?.split('.')?.at(-1);

        if (!currentNodeId) {
            return { nodeIds: [], projectId };
        }

        const graph = selectGroupedDependencyGraph(state);
        const paths = graph.findAllPaths({ from: currentNodeId, to });
        const nodeIds = [...new Set(paths.flat())];

        return { nodeIds, projectId };
    }
});

export const doClearSelection = () => ({
    type: CLEAR_SELECTION,
    payload: (state) => {
        const projectId = selectCurrentProjectId(state);
        return { projectId };
    }
});

export const doToggleGroup = (assetId) => ({
    type: TOGGLE_GROUP,
    payload: { assetId }
});

export const doSetBulkGroupExpansion = (expandedById) => ({
    type: SET_BULK_GROUP_EXPANSION,
    payload: { expandedById }
});

export const doExpandAllGroups = () => ({
    type: EXPAND_ALL_GROUPS,
    payload: (state) => {
        const groups = selectGroups(state);

        return {
            assetIds: groups.map((group) => `${group.spaceUid}.${group.id}`)
        };
    }
});

export const doCollapseAllGroups = () => ({
    type: COLLAPSE_ALL_GROUPS,
    payload: (state) => {
        const groups = selectGroups(state);

        return {
            assetIds: groups.map((group) => `${group.spaceUid}.${group.id}`)
        };
    }
});

export const doCreateNodeGroup = () => ({
    type: CREATE_NODE_GROUP,
    api: { version: 2, name: 'CreateGroup' },
    params: (state) => {
        const project = selectProject(state);
        const selection = selectSelectedNodesForCurrentProject(state);

        return {
            spaceUid: project.uid,
            children: selection
        };
    },
    onSucceeded: ({ getState, payload }) => {
        const state = getState();
        const project = selectEntitiesById(state)[payload.spaceUid];
        navigateToWorkbench(project.slug, payload.group.id);
    }
});

export const doDeleteNodeGroup = (entityId) => ({
    type: DELETE_NODE_GROUP,
    api: { version: 2, name: 'DeleteGroup' },
    params: () => {
        const [spaceUid, groupUid] = entityId?.split('.') ?? [];
        return {
            spaceUid,
            groupUid
        };
    },
    onSucceeded: ({ getState, payload }) => {
        const state = getState();
        const project = selectEntitiesById(state)[payload.spaceUid];
        navigateToWorkbench(project.slug);
    }
});

export const doRenameNodeGroup = ({ entityId, name }) => ({
    type: RENAME_NODE_GROUP,
    api: { version: 2, name: 'UpdateGroupName' },
    params: () => {
        const [spaceUid, groupUid] = entityId?.split('.') ?? [];
        return {
            spaceUid,
            groupUid,
            name
        };
    },
    onSucceeded: ({ getState, payload }) => {
        const state = getState();
        const project = selectEntitiesById(state)[payload.spaceUid];
        navigateToWorkbench(project.slug, payload.groupUid);
    }
});

export const doUpdateGroupDescription = ({ assetId, description }) => ({
    type: UPDATE_NODE_GROUP_DESCRIPTION,
    api: { version: 2, name: 'UpdateGroupDescription' },
    params: () => {
        const [spaceUid, groupUid] = assetId?.split('.') ?? [];
        return {
            spaceUid,
            groupUid,
            description
        };
    }
});

export const doNavigateToNode = (id) => ({
    type: NAVIGATE_TO_NODE,
    effect: ({ store }) => {
        const state = store.getState();
        const project = selectProject(state);
        const view = selectEntityByPropId(state, { entityId: `${project.uid}.${id}` });

        navigateToWorkbench(project.slug, view.uid);
    }
});
